import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Course } from '../types';
import { FiArrowLeft } from 'react-icons/fi';

const CourseSchedulePage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSemester, setSelectedSemester] = useState('Fall 2025');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      if (!currentUser) return;

      try {
        const coursesRef = collection(db, 'courses');
        const q = query(
          coursesRef,
          where('userId', '==', currentUser.uid),
          where('semester', '==', selectedSemester),
          where('status', '==', 'enrolled')
        );

        const querySnapshot = await getDocs(q);
        const coursesData = querySnapshot.docs.map((doc) => ({
          courseId: doc.id,
          ...doc.data(),
        })) as Course[];

        setCourses(coursesData);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [currentUser, selectedSemester]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-uc-navy text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/home')}
                className="mr-4 hover:text-uc-gold transition-colors flex items-center"
              >
                <FiArrowLeft size={20} className="mr-2" />
                Back
              </button>
              <h1 className="text-2xl font-bold font-header">Course Schedule</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-6">
          {/* Semester Selector */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2 text-sm">
              Select Term:
            </label>
            <select
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-uc-navy text-sm"
            >
              <option value="Fall 2025">Fall 2025</option>
              <option value="Spring 2025">Spring 2025</option>
              <option value="Summer 2025">Summer 2025</option>
              <option value="Fall 2024">Fall 2024</option>
              <option value="Spring 2024">Spring 2024</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uc-navy"></div>
              <p className="mt-2 text-gray-600">Loading courses...</p>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No courses found for {selectedSemester}</p>
              <p className="text-sm text-gray-500 mt-2">
                Sample courses will appear here when added to Firestore
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-uc-navy text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Course Code</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Course Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Instructor</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Days</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Location</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course, index) => (
                    <tr
                      key={course.courseId}
                      className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                    >
                      <td className="px-4 py-3 font-semibold text-uc-navy text-sm">
                        {course.courseCode}
                      </td>
                      <td className="px-4 py-3 text-sm">{course.courseName}</td>
                      <td className="px-4 py-3 text-sm">{course.instructor}</td>
                      <td className="px-4 py-3 text-sm">
                        {course.schedule.days.join(', ')}
                      </td>
                      <td className="px-4 py-3 text-sm">{course.schedule.time}</td>
                      <td className="px-4 py-3 text-sm">{course.schedule.location}</td>
                      <td className="px-4 py-3 text-center text-sm">{course.credits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Summary */}
              <div className="mt-6 p-4 bg-gray-50 rounded">
                <p className="font-semibold text-uc-navy">
                  Total Credits: {courses.reduce((sum, course) => sum + course.credits, 0)}
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CourseSchedulePage;
